import { Box, Button, Typography } from "@mui/material";
import moment from "moment";
import { TFunction } from "react-i18next";
import { NavigateFunction } from "react-router-dom";

import { WrapperCard } from "../WrapperCard/WrapperCard";
import tokens from "src/shared/theme/tokens";
import {
  ApplicationAction,
  getApplicationAction,
} from "src/helpers/helperApplications";

interface PendingApplicationsCardProps {
  /** Applications already filtered by `selectPendingApplications`. */
  applications: any[];
  navigate: NavigateFunction;
  t: TFunction;
}

/**
 * Badge label + colors. Driven by the actionable state, not the raw status:
 * a paid ("completed") application that still owes income verification is not
 * really done, so it reads "Income needed" (amber) rather than "Completed".
 */
const getBadge = (
  action: ApplicationAction | null,
  status: string,
  t: TFunction
): { label: string; bg: string; fg: string } => {
  if (action?.kind === "verify-income") {
    return {
      label: t("home.pendingApplications.statusIncomeNeeded", "Income needed"),
      bg: tokens.colors.warning.bg,
      fg: tokens.colors.warning.fg,
    };
  }
  if (status === "pending") {
    return {
      label: status,
      bg: tokens.colors.warning.bg,
      fg: tokens.colors.warning.fg,
    };
  }
  return {
    label: status,
    bg: tokens.colors.info.bg,
    fg: tokens.colors.info.fg,
  };
};

export const PendingApplicationsCard = ({
  applications,
  navigate,
  t,
}: PendingApplicationsCardProps) => {
  if (!applications || applications.length === 0) return null;

  return (
    <WrapperCard
      title={t("home.pendingApplications.title", "Your applications")}
    >
      <Box sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
        <Typography
          sx={{
            fontFamily: tokens.fonts.body,
            fontSize: "0.85rem",
            color: tokens.colors.inkMuted,
            mb: 2,
          }}
        >
          {t(
            "home.pendingApplications.subtitle",
            "These applications still need your attention."
          )}
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {applications.map((app: any, index: number) => {
            const action = getApplicationAction(app);
            const status: string = app?.status ?? "";
            const badge = getBadge(action, status, t);
            const propName =
              app?.property?.name ||
              app?.propertyName ||
              t("home.pendingApplications.propertyFallback", "Property");

            return (
              <Box
                key={app?.id ?? index}
                sx={{
                  border: `1px solid ${tokens.colors.border}`,
                  borderRadius: `${tokens.radius.md}px`,
                  backgroundColor: tokens.colors.surface,
                  p: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 1,
                    mb: 0.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: tokens.fonts.display,
                      fontWeight: 600,
                      color: tokens.colors.ink,
                    }}
                  >
                    {propName}
                  </Typography>
                  <Box
                    sx={{
                      backgroundColor: badge.bg,
                      color: badge.fg,
                      px: 1.25,
                      py: 0.5,
                      borderRadius: `${tokens.radius.pill}px`,
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {badge.label}
                  </Box>
                </Box>

                {app?.unit && app.unit !== "HOLD" && (
                  <Typography
                    sx={{
                      fontSize: "0.85rem",
                      color: tokens.colors.inkSecondary,
                    }}
                  >
                    {t("home.pendingApplications.unit", "Unit")}: {app.unit}
                  </Typography>
                )}
                {app?.moveInDate && (
                  <Typography
                    sx={{
                      fontSize: "0.85rem",
                      color: tokens.colors.inkSecondary,
                    }}
                  >
                    {t("home.pendingApplications.moveInDate", "Move-in date")}:{" "}
                    {moment(app.moveInDate).format("MMM DD, YYYY")}
                  </Typography>
                )}

                {action && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => navigate(action.buildPath())}
                    sx={{
                      mt: 1.5,
                      textTransform: "none",
                      borderColor: tokens.colors.primary,
                      color: tokens.colors.primary,
                      "&:hover": {
                        borderColor: tokens.colors.primaryDeep,
                        backgroundColor: tokens.colors.primarySoft,
                      },
                    }}
                  >
                    {t(action.labelKey, action.labelFallback)}
                  </Button>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    </WrapperCard>
  );
};
